import type { CustomerModel } from 'src/types/customer';
import type { PaginatorResponse } from 'src/types/common';

import { useRef, useState, useEffect } from 'react';

import axios, { endpoints } from 'src/lib/axios';

export interface CustomerFilters {
  id?: number;
  name?: string;
  email?: string;
  company?: string;
  country?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

interface UseCustomersSearchProps {
  searchQuery?: string;
  initialData?: CustomerModel[];
  debounceTime?: number;
}

/**
 * Hook para búsqueda de clientes con soporte para diferentes casos de uso:
 * 1. Búsqueda simple por nombre: useCustomers({ searchQuery: 'texto' })
 * 2. Búsqueda paginada con filtros: useCustomers(page, pageSize, filters)
 * 3. Con refreshKey para forzar recarga: useCustomers(page, pageSize, filters, refreshKey)
 */
export function useCustomers(
  pageOrSearchProps: number | UseCustomersSearchProps = 1,
  pageSize: number = 25,
  filters?: CustomerFilters,
  refreshKey?: number
) {
  const [customers, setCustomers] = useState<PaginatorResponse<CustomerModel> | undefined>(
    undefined
  );
  const [data, setData] = useState<CustomerModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Ref para controlar el número de solicitudes y prevenir bucles infinitos
  const requestCountRef = useRef(0);
  const previousRequestRef = useRef<string>('');

  useEffect(() => {
    // Determinar si estamos en modo búsqueda simple o paginación
    const isSearchMode = typeof pageOrSearchProps === 'object';
    const requestKey = isSearchMode
      ? `search-${(pageOrSearchProps as UseCustomersSearchProps).searchQuery || ''}-${refreshKey || 0}`
      : `paginated-${pageOrSearchProps}-${pageSize}-${JSON.stringify(filters || {})}-${refreshKey || 0}`;

    // Si la solicitud es la misma que la anterior, no hacemos nada
    if (requestKey === previousRequestRef.current) {
      return;
    }

    previousRequestRef.current = requestKey;

    // Incrementamos el contador de solicitudes
    requestCountRef.current += 1;
    const currentRequestCount = requestCountRef.current;

    const fetchCustomers = async () => {
      try {
        setIsLoading(true);

        // Modo búsqueda simple (para autocompletado)
        if (isSearchMode) {
          const { searchQuery = '' } = pageOrSearchProps as UseCustomersSearchProps;

          if (!searchQuery.trim()) {
            setData([]);
            setIsLoading(false);
            return;
          }

          const response = await axios.post(endpoints.customers.getAll, {
            page: 1,
            pageSize: 10,
            name: searchQuery,
            email: searchQuery,
          });

          // Solo actualizar si esta es la solicitud más reciente
          if (currentRequestCount === requestCountRef.current) {
            setData(response.data.data || []);
          }
        }
        // Modo paginado con filtros (para tablas)
        else {
          const page = pageOrSearchProps as number;

          const response = await axios.post(endpoints.customers.getAll, {
            page,
            pageSize,
            ...filters,
          });

          // Solo actualizar si esta es la solicitud más reciente
          if (currentRequestCount === requestCountRef.current) {
            setCustomers(response.data);
          }
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
        setError(err instanceof Error ? err : new Error('Error al cargar los clientes'));
      } finally {
        if (currentRequestCount === requestCountRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchCustomers();

    // Limpieza - no es necesario hacer nada específico aquí
  }, [pageOrSearchProps, pageSize, filters, refreshKey]);

  // Para el modo de búsqueda simple
  if (typeof pageOrSearchProps === 'object') {
    return {
      data,
      loading: isLoading,
      error,
    };
  }

  // Para el modo de paginación con filtros
  return {
    customers,
    isLoading,
    error,
  };
}
